import { Fragment, Suspense, lazy, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onAuthStateChanged } from "firebase/auth";
import AdminLayout from "./layouts/AdminLayout";
import MainLayout from "./layouts/MainLayout";
// import NoMatch from "./views/NoMatchPage/NoMatch";
import { auth as firebaseAuth } from "./conf/firebase";

import dashRoutes from "./routes/dash";
import { onAuthChange, resetAuth } from "./redux/actions";
import Login from "./views/auth/Login";
import Recovery from "./views/auth/Recovery";
import NoMatch from "./views/NoMatchPage/NoMatch";
// import ForgotPassword from "./views/auth/ForgotPassword";
// import ResetPassword from "./views/auth/ResetPassword";
import ProtectedRoute from "./routes/ProtectedRoute";
import AuthLayout from "./layouts/AuthLayout";

// Dev-only harness for the training module (demo data, no login). See views/training/TrainingPreview.jsx.
// Guarded by NODE_ENV so the harness and the client's demo fixtures never reach a production bundle.
const TrainingPreview =
  process.env.NODE_ENV !== "production" && process.env.REACT_APP_TRAINING_DEV === "true"
    ? lazy(() => import("./views/training/TrainingPreview"))
    : null;
const TRAINING_DEV = TrainingPreview !== null;

const App = () => {
  const { auth } = useSelector((state) => state.auth.auth);
  const { user } = useSelector((state) => state.user);

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        dispatch(onAuthChange(user));
      } else {
        dispatch(resetAuth());
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    switch (location.pathname) {
      case "/":
        if (auth && auth?.uid && !auth?.isAnonymous) {
          navigate("/dashboard");
        } else {
          navigate("/login");
        }
        break;
      case "/login":
        if (auth && auth?.uid && !auth?.isAnonymous) {
          navigate("/dashboard");
        }
        break;
      default:
        break;
    }
  }, [auth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Routes>
      {TRAINING_DEV && (
        <Route
          path="/training-preview/*"
          element={
            <Suspense fallback={null}>
              <TrainingPreview />
            </Suspense>
          }
        />
      )}
      <Route
        path="/"
        exact
        element={<MainLayout />}
      >
        <Route element={<AdminLayout />}>
          {dashRoutes.map((route) => (
            <Fragment key={route.name}>
              <Route
                path={route.path}
                element={
                  <ProtectedRoute
                    user={user}
                    route_permissions={route.permissions}
                  >
                    {route.element}
                  </ProtectedRoute>
                }
              >
                {route?.subRoutes &&
                  route.subRoutes.map((subRoute) => (
                    <Route
                      key={route.name}
                      path={subRoute.path}
                      element={
                        <ProtectedRoute
                          user={user}
                          route_permissions={subRoute.permissions}
                        >
                          {subRoute.element}
                        </ProtectedRoute>
                      }
                    />
                  ))}
              </Route>
              {route?.children &&
                route.children.map((childRoute) => (
                  <Route
                    key={childRoute.name}
                    path={childRoute.path}
                    element={
                      <ProtectedRoute
                        user={user}
                        route_permissions={childRoute.permissions}
                      >
                        {childRoute.element}
                      </ProtectedRoute>
                    }
                  >
                    {childRoute?.subRoutes &&
                      childRoute.subRoutes.map((subRoute) => (
                        <Route
                          key={route.name}
                          path={subRoute.path}
                          element={
                            <ProtectedRoute
                              user={user}
                              route_permissions={subRoute.permissions}
                            >
                              {subRoute.element}
                            </ProtectedRoute>
                          }
                        />
                      ))}
                  </Route>
                ))}
            </Fragment>
          ))}
        </Route>

        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={<Login />}
          />
          <Route
            path="/recovery"
            element={<Recovery />}
          />
        </Route>
        {/* <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:userUid/:token" element={<ResetPassword />} /> */}
        <Route
          path="*"
          element={<NoMatch />}
        />
      </Route>
    </Routes>
  );
};

export default App;
